import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Row, Col, Card } from "react-bootstrap";
import CalendarSection from "./CalendarSection";
import DetailSection from "./DetailSection";
import { ListaAsistenciaProyectada } from "../../../services/asistenciaServicios";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { dateCompareStr, obtenerStringDeFecha } from "../../../utils/dateUtils";
import {
  ListaAsistenciaProyectadaSpa,
  useObtenerListasAsistenciasProyectadasSpa,
} from "../../../services/asistenciaSpaServicios";

function MasterDetailSection() {
  const {
    data: listasAsistenciaProyectadas,
    isLoading: listasAsistenciaProyectadasIsLoading,
    error: listasAsistenciaProyectadasError,
  } = useObtenerListasAsistenciasProyectadasSpa();

  const [animationParent] = useAutoAnimate<HTMLDivElement>();
  const [detailAnimationParent] = useAutoAnimate<HTMLDivElement>();

  const [fechaSeleccionada, setFechaSeleccionada] = useState<Date | undefined>(
    undefined
  );

  const [listaProyectada, setListaProyectada] = useState<
    ListaAsistenciaProyectadaSpa | undefined
  >(undefined);

  const listasProyectadas = useMemo(() => {
    return (
      listasAsistenciaProyectadas || new Array<ListaAsistenciaProyectadaSpa>()
    );
  }, [listasAsistenciaProyectadas]);

  const listasProyectadasOrdenadas = useMemo(() => {
    let ordenadas = listasProyectadas;

    ordenadas.sort((listaA, listaB) => {
      if (listaA.fecha > listaB.fecha) {
        return 1;
      } else if (listaB.fecha > listaA.fecha) {
        return -1;
      } else {
        return 0;
      }
    });

    return ordenadas;
  }, [listasProyectadas]);

  useEffect(() => {
    if (listasAsistenciaProyectadas && fechaSeleccionada) {
      const listaSeleccionada = buscarListaProyectada(
        listasProyectadasOrdenadas,
        fechaSeleccionada
      );
      if (!listaSeleccionada) {
        setListaProyectada(undefined);
      } else {
        setListaProyectada(listaSeleccionada);
      }
    } else {
      setListaProyectada(undefined);
    }
  }, [
    listasAsistenciaProyectadas,
    fechaSeleccionada,
    listasProyectadasOrdenadas,
  ]);

  const onDateClick = (date: Date) => {
    setFechaSeleccionada(date);
  };

  return (
    <StyledRow>
      <Col className="col-12">
        <StyledCard>
          <StyledCardBody>
            <Row ref={animationParent}>
              <Col md={listaProyectada ? "6" : "12"} sm="12">
                <CalendarSection
                  onDateClick={onDateClick}
                  listasAsistenciaProyectadasError={
                    listasAsistenciaProyectadasError
                  }
                  listasAsistenciaProyectadasIsLoading={
                    listasAsistenciaProyectadasIsLoading
                  }
                  listasProyectadasOrdenadas={listasProyectadasOrdenadas}
                />
              </Col>
              {listaProyectada && (
                <Col md="6" sm="12" ref={detailAnimationParent}>
                  <DetailSection listaProyectada={listaProyectada} />
                </Col>
              )}
            </Row>
          </StyledCardBody>
        </StyledCard>
      </Col>
    </StyledRow>
  );
}

const StyledRow = styled(Row)`
  justify-content: center;
`;

const StyledCard = styled(Card)`
  margin-bottom: 1.875rem;
  background-color: #fff;
  transition: all 0.5s ease-in-out;
  position: relative;
  border: 0px solid transparent;
  border-radius: 0.25rem;
  box-shadow: 0px 0px 13px 0px rgb(82 63 105 / 5%);
`;

const StyledCardBody = styled(Card.Body)`
  padding: 1.25rem;
`;

function buscarIndiceListaProyectada(
  listasProyectadasOrdenadas: ListaAsistenciaProyectada[],
  fecha: Date
): number {
  let m = 0;
  let n = listasProyectadasOrdenadas.length - 1;
  while (m <= n) {
    let k = (n + m) >> 1;
    let cmp = dateCompareStr(obtenerStringDeFecha(fecha), listasProyectadasOrdenadas[k].fecha);
    if (cmp > 0) {
      m = k + 1;
    } else if (cmp < 0) {
      n = k - 1;
    } else {
      return k;
    }
  }
  return ~m;
}

function buscarListaProyectada(
  listasProyectadasOrdenadas: ListaAsistenciaProyectadaSpa[],
  fecha: Date
): ListaAsistenciaProyectadaSpa | null {
  const indice = buscarIndiceListaProyectada(listasProyectadasOrdenadas, fecha);
  if (indice < 0) {
    return null;
  }
  return listasProyectadasOrdenadas[indice];
}

export default MasterDetailSection;
